import type { Attachment } from 'svelte/attachments';

export type ObserverCallback = (entry: IntersectionObserverEntry) => void;

class TileObserver {
    private beforeEnterObserver?: IntersectionObserver;
    private onEnterObserver?: IntersectionObserver;
    private beforeEnterCallbacks = new WeakMap<Element, ObserverCallback>();
    private onEnterCallbacks = new WeakMap<Element, ObserverCallback>();
    private initialized = false;

    observeBeforeEnter(callback: ObserverCallback): Attachment {
        return (element) => {
            this.initialize();

            if (!this.beforeEnterObserver) return () => {};
            this.beforeEnterCallbacks.set(element, callback);
            this.beforeEnterObserver.observe(element);

            return () => {
                this.beforeEnterCallbacks.delete(element);
                this.beforeEnterObserver?.unobserve(element);
            };
        };
    }

    observeOnEnter(callback: ObserverCallback): Attachment {
        return (element) => {
            this.initialize();

            if (!this.onEnterObserver) return () => {};
            this.onEnterCallbacks.set(element, callback);
            this.onEnterObserver.observe(element);

            return () => {
                this.onEnterCallbacks.delete(element);
                this.onEnterObserver?.unobserve(element);
            };
        };
    }

    private initialize() {
        if (this.initialized || typeof IntersectionObserver === 'undefined') {
            return;
        }

        this.beforeEnterObserver = new IntersectionObserver(
            (entries) => this.handleIntersection(entries, this.beforeEnterCallbacks),
            { root: null, rootMargin: '300px 0px 300px 0px', threshold: 0 },
        );

        this.onEnterObserver = new IntersectionObserver(
            (entries) => this.handleIntersection(entries, this.onEnterCallbacks),
            { root: null, threshold: 1.0 },
        );

        this.initialized = true;
    }

    private handleIntersection(entries: IntersectionObserverEntry[], callbacks: WeakMap<Element, ObserverCallback>) {
        for (const entry of entries) {
            const callback = callbacks.get(entry.target);
            if (callback) {
                callback(entry);
                /*                                                       */
            }
        }
    }
}

export const tileObserver = new TileObserver();
